import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Handshake, PhoneIncoming, PhoneOutgoing, PhoneMissed, Users, StickyNote,
  Loader2, Search, FileAudio, ChevronDown, X, Check, Plus, Link2, Pencil, Save, Trash2, RefreshCw,
} from 'lucide-react';

const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, '0')} min`;
const fmtDate = (at) => {
  if (!at) return '—';
  const d = new Date(at);
  if (isNaN(d)) return at;
  const ds = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return at.length > 10 ? `${ds}, ${d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}` : ds;
};

const daysAgo = (at) => {
  if (!at) return null;
  const d = new Date(at);
  if (isNaN(d)) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};
const lastLabel = (at) => {
  const n = daysAgo(at);
  if (n === null) return '—';
  if (n <= 0) return 'heute';
  if (n === 1) return 'gestern';
  return `vor ${n} Tagen`;
};

function KindIcon({ e }) {
  if (e.kind === 'call') {
    if (e.direction === 'in' && !e.answered) return <PhoneMissed className="w-4 h-4 text-red-400 shrink-0" />;
    if (e.direction === 'in') return <PhoneIncoming className="w-4 h-4 text-emerald-400 shrink-0" />;
    return <PhoneOutgoing className="w-4 h-4 text-sky-400 shrink-0" />;
  }
  if (e.kind === 'meeting') return <Users className="w-4 h-4 text-violet-400 shrink-0" />;
  return <StickyNote className="w-4 h-4 text-muted-foreground shrink-0" />;
}

function PlaudPicker({ recordings, loading, busyRec, onPick, onClose }) {
  return (
    <div className="mt-1.5 border border-violet-500/25 bg-card rounded-sm max-w-lg" data-testid="plaud-picker">
      <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-border">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Plaud-Aufnahmen — letzte 14 Tage</span>
        <button onClick={onClose} data-testid="plaud-picker-close" className="text-muted-foreground hover:text-foreground transition-colors">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
      ) : recordings === null ? (
        <p className="px-2.5 py-3 text-[11px] text-muted-foreground">
          Aufnahmen konnten nicht geladen werden — ist Plaud verbunden? (Team-Meeting-Seite → Plaud-Einstellungen)
        </p>
      ) : recordings.length === 0 ? (
        <p className="px-2.5 py-3 text-[11px] text-muted-foreground">Keine Aufnahmen in den letzten 14 Tagen gefunden.</p>
      ) : (
        <div className="max-h-56 overflow-y-auto divide-y divide-border">
          {recordings.map(r => (
            <button key={r.id} onClick={() => onPick(r)} disabled={!!busyRec}
              data-testid={`plaud-picker-item-${r.id}`}
              className="w-full text-left px-2.5 py-2 hover:bg-secondary/60 transition-colors disabled:opacity-50">
              <div className="flex items-center gap-2">
                {busyRec === r.id
                  ? <Loader2 className="w-3 h-3 animate-spin text-violet-300 shrink-0" />
                  : <FileAudio className="w-3 h-3 text-violet-300 shrink-0" />}
                <span className="text-xs text-foreground truncate flex-1">{r.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                  {fmtDate(r.created_at)}{r.duration_min ? ` · ${r.duration_min} min` : ''}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function DealSuggestions({ e, idx, linkBusy, onConfirm, onDismiss, onCreateFromInbox }) {
  const deals = e.plaud?.deal_suggestions || [];
  const inbox = e.plaud?.inbox_suggestions || [];
  if (deals.length === 0 && inbox.length === 0) return null;
  return (
    <div className="mt-1.5 border border-sky-500/25 bg-sky-500/5 rounded-sm px-2.5 py-2 space-y-1.5 max-w-lg"
      data-testid={`broker-call-deal-suggestions-${idx}`}>
      <p className="text-[10px] uppercase tracking-wide text-sky-300">KI-Vorschlag — besprochene Deals</p>
      {deals.map(s => (
        <div key={s.project_id} className="flex items-center gap-2 flex-wrap text-xs">
          <Link to={`/projects/${s.project_id}`} className="text-sky-400 hover:underline truncate">{s.name}</Link>
          {s.company && s.company !== s.name &&
            <span className="text-[10px] text-muted-foreground truncate">({s.company})</span>}
          <button onClick={() => onConfirm(e, s)} disabled={!!linkBusy}
            data-testid={`confirm-deal-link-${s.project_id}`}
            className="flex items-center gap-1 text-[10px] border border-sky-500/40 rounded-sm px-2 py-0.5 text-sky-300 hover:bg-sky-500/15 transition-colors disabled:opacity-50">
            {linkBusy === s.project_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Mit Deal verknüpfen
          </button>
          <button onClick={() => onDismiss(e, s)} title="Vorschlag verwerfen"
            data-testid={`dismiss-deal-link-${s.project_id}`}
            className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      {inbox.map(s => (
        <div key={s.proposal_id} className="flex items-center gap-2 flex-wrap text-xs">
          <Link to="/deal-inbox" className="text-amber-300 hover:underline truncate">
            In der Deal-Inbox: {s.company || s.subject}
          </Link>
          <button onClick={() => onCreateFromInbox(e, s)} disabled={!!linkBusy}
            data-testid={`create-link-inbox-${s.proposal_id}`}
            className="flex items-center gap-1 text-[10px] border border-amber-500/40 rounded-sm px-2 py-0.5 text-amber-300 hover:bg-amber-500/15 transition-colors disabled:opacity-50">
            {linkBusy === s.proposal_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Deal anlegen + verknüpfen
          </button>
          <button onClick={() => onDismiss(e, s)} title="Vorschlag verwerfen"
            data-testid={`dismiss-inbox-link-${s.proposal_id}`}
            className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}


function AddMeetingDialog({ open, onOpenChange, onDone }) {
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [contact, setContact] = useState(null);
  const [createNew, setCreateNew] = useState(false);
  const [nc, setNc] = useState({ first_name: '', last_name: '', company: '', email: '' });
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [duration, setDuration] = useState('30');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [dealSearch, setDealSearch] = useState('');
  const [dealHits, setDealHits] = useState([]);
  const [deal, setDeal] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    setDate(now.toISOString().slice(0, 10));
    setTime(now.toTimeString().slice(0, 5));
    setContact(null); setCreateNew(false); setSearch(''); setHits([]);
    setNc({ first_name: '', last_name: '', company: '', email: '' });
    setDuration('30'); setTitle(''); setNotes('');
    setDeal(null); setDealSearch(''); setDealHits([]);
  }, [open]);

  useEffect(() => {
    if (!open || contact || createNew) return;
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      api.getContacts(search, 'Broker')
        .then(r => { if (!cancelled) setHits((r.data.contacts || []).slice(0, 8)); })
        .catch(() => { if (!cancelled) setHits([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, search, contact, createNew]);

  useEffect(() => {
    if (!open || deal || !dealSearch.trim()) { setDealHits([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.compareSearch(dealSearch)
        .then(r => { if (!cancelled) setDealHits((r.data.projects || r.data || []).slice(0, 6)); })
        .catch(() => { if (!cancelled) setDealHits([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, dealSearch, deal]);

  const save = async () => {
    if (!contact && !createNew) { toast.error('Bitte einen Broker auswählen oder neu anlegen'); return; }
    if (createNew && !nc.last_name.trim() && !nc.company.trim()) { toast.error('Nachname oder Firma des Brokers angeben'); return; }
    if (!date || !time) { toast.error('Datum und Uhrzeit angeben'); return; }
    setSaving(true);
    try {
      await api.addBrokerMeeting({
        contact_id: contact?.id || '',
        new_contact: createNew ? nc : null,
        at: new Date(`${date}T${time}`).toISOString(),
        duration_min: parseInt(duration, 10) || 0,
        title: title.trim(), notes: notes.trim(),
        project_id: deal?.id || '',
      });
      toast.success('Meeting erfasst — jetzt kann die Plaud-Notiz angehängt werden');
      onOpenChange(false); onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const inputCls = 'bg-secondary border-border text-foreground rounded-sm h-8 text-xs';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
            <Users className="w-4 h-4 text-violet-400" /> Broker-Meeting erfassen
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-muted-foreground -mt-1">
          Z.B. ein Teams-Meeting — erscheint in der Liste und kann wie ein Call eine Plaud-Notiz bekommen.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Broker</label>
            {contact ? (
              <div className="flex items-center gap-2 border border-violet-500/40 bg-violet-500/10 rounded-sm px-2.5 py-1.5" data-testid="meeting-selected-broker">
                <span className="text-xs text-foreground">{[contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.company}</span>
                {contact.company && <span className="text-[10px] text-muted-foreground truncate">· {contact.company}</span>}
                <button onClick={() => setContact(null)} className="ml-auto text-muted-foreground hover:text-red-400" data-testid="meeting-broker-clear">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : createNew ? (
              <div className="space-y-1.5 border border-border rounded-sm p-2.5" data-testid="meeting-new-broker-form">
                <div className="grid grid-cols-2 gap-1.5">
                  <Input value={nc.first_name} onChange={e => setNc(v => ({ ...v, first_name: e.target.value }))}
                    placeholder="Vorname" data-testid="meeting-nc-firstname" className={inputCls} />
                  <Input value={nc.last_name} onChange={e => setNc(v => ({ ...v, last_name: e.target.value }))}
                    placeholder="Nachname" data-testid="meeting-nc-lastname" className={inputCls} />
                </div>
                <Input value={nc.company} onChange={e => setNc(v => ({ ...v, company: e.target.value }))}
                  placeholder="Firma (z.B. Belgravia & Co.)" data-testid="meeting-nc-company" className={inputCls} />
                <Input value={nc.email} onChange={e => setNc(v => ({ ...v, email: e.target.value }))}
                  placeholder="E-Mail (optional)" data-testid="meeting-nc-email" className={inputCls} />
                <button onClick={() => setCreateNew(false)} className="text-[10px] text-muted-foreground hover:text-foreground">
                  Zurück zur Suche
                </button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Broker-Kontakt suchen…"
                    data-testid="meeting-broker-search" className={`${inputCls} pl-8`} />
                </div>
                <div className="border border-border rounded-sm divide-y divide-border mt-1 max-h-40 overflow-y-auto">
                  {searching ? (
                    <div className="flex justify-center py-3"><Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" /></div>
                  ) : hits.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground text-center py-3">Keine Broker-Kontakte gefunden.</p>
                  ) : hits.map(c => (
                    <button key={c.id} onClick={() => setContact(c)} data-testid={`meeting-broker-hit-${c.id}`}
                      className="w-full text-left px-2.5 py-1.5 hover:bg-secondary/40 transition-colors">
                      <span className="text-xs text-foreground">{[c.first_name, c.last_name].filter(Boolean).join(' ') || c.company}</span>
                      {c.company && <span className="text-[10px] text-muted-foreground ml-1.5">· {c.company}</span>}
                    </button>
                  ))}
                </div>
                <button onClick={() => setCreateNew(true)} data-testid="meeting-create-broker-btn"
                  className="flex items-center gap-1 mt-1 text-[10px] text-violet-300 hover:text-violet-200 transition-colors">
                  <Plus className="w-3 h-3" /> Broker nicht im CRM? Neu anlegen…
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Datum</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="meeting-date"
                className="w-full h-8 px-2 text-xs bg-secondary border border-border rounded-sm text-foreground" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Uhrzeit</label>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} data-testid="meeting-time"
                className="w-full h-8 px-2 text-xs bg-secondary border border-border rounded-sm text-foreground" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Dauer (Min.)</label>
              <input type="number" min="0" value={duration} onChange={e => setDuration(e.target.value)} data-testid="meeting-duration"
                className="w-full h-8 px-2 text-xs font-mono bg-secondary border border-border rounded-sm text-foreground" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Titel</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Teams-Intro Belgravia"
              data-testid="meeting-title" className={inputCls} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Notizen (optional)</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Kurze Gesprächsnotizen — oder später die Plaud-Notiz anhängen…"
              data-testid="meeting-notes" className="bg-secondary border-border text-foreground rounded-sm text-xs" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Deal verknüpfen (optional)</label>
            {deal ? (
              <div className="flex items-center gap-2 border border-sky-500/40 bg-sky-500/10 rounded-sm px-2.5 py-1.5" data-testid="meeting-selected-deal">
                <span className="text-xs text-foreground truncate">{deal.name}</span>
                <button onClick={() => setDeal(null)} className="ml-auto text-muted-foreground hover:text-red-400" data-testid="meeting-deal-clear">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input value={dealSearch} onChange={e => setDealSearch(e.target.value)} placeholder="Nach Deal-Name suchen…"
                    data-testid="meeting-deal-search" className={`${inputCls} pl-8`} />
                </div>
                {dealHits.length > 0 && (
                  <div className="border border-border rounded-sm divide-y divide-border mt-1 max-h-32 overflow-y-auto">
                    {dealHits.map(p => (
                      <button key={p.id} onClick={() => setDeal(p)} data-testid={`meeting-deal-hit-${p.id}`}
                        className="w-full text-left px-2.5 py-1.5 hover:bg-secondary/40 transition-colors">
                        <span className="text-xs text-foreground truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-xs h-8" data-testid="meeting-cancel">Abbrechen</Button>
            <Button onClick={save} disabled={saving} data-testid="meeting-save"
              className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-8">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Meeting speichern
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BrokerCallsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState('');
  const [plaudPickerFor, setPlaudPickerFor] = useState(null);
  const [recordings, setRecordings] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);
  const [busyRec, setBusyRec] = useState('');
  const [linkBusy, setLinkBusy] = useState('');
  const [manualLinkFor, setManualLinkFor] = useState(null); // engagement obj
  const [manualSearch, setManualSearch] = useState('');
  const [manualHits, setManualHits] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [editFor, setEditFor] = useState(null); // engagement obj (Plaud note edit)
  const [editSummary, setEditSummary] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [calSyncing, setCalSyncing] = useState(false);

  const syncCalendar = async () => {
    setCalSyncing(true);
    try {
      const r = await api.syncBrokerCalendar();
      const n = r.data.imported || 0;
      toast.success(n > 0 ? `${n} Broker-Meeting${n === 1 ? '' : 's'} aus dem Kalender übernommen` : 'Keine neuen Broker-Meetings im Kalender gefunden');
      if (n > 0) load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Kalender-Scan fehlgeschlagen'); }
    finally { setCalSyncing(false); }
  };
  const pollCount = useRef(0);

  // Deal search for manual link — hits `compare-search` (fast, up to 50 results).
  useEffect(() => {
    if (!manualLinkFor) return;
    let cancelled = false;
    setManualLoading(true);
    const t = setTimeout(() => {
      api.compareSearch(manualSearch)
        .then(r => { if (!cancelled) setManualHits(r.data.projects || r.data || []); })
        .catch(() => { if (!cancelled) setManualHits([]); })
        .finally(() => { if (!cancelled) setManualLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [manualLinkFor, manualSearch]);

  const openManualLink = (e) => { setManualLinkFor(e); setManualSearch(''); setManualHits([]); };
  const doManualLink = async (proj) => {
    if (!manualLinkFor) return;
    setLinkBusy(proj.id);
    try {
      const r = await api.brokerConfirmDealLink(linkBody(manualLinkFor, { target_project_id: proj.id }));
      toast.success(r.data.mandate_linked
        ? `Mit „${r.data.project_name}" verknüpft — Gespräch auf dem Deal & Mandat dem Broker zugeordnet`
        : `Mit „${r.data.project_name}" verknüpft — das Gespräch erscheint jetzt auf dem Deal`);
      setManualLinkFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Verknüpfen fehlgeschlagen'); }
    finally { setLinkBusy(''); }
  };

  const openEdit = (e) => {
    setEditFor(e);
    setEditTitle(e.plaud?.title || '');
    setEditSummary(e.plaud?.summary || '');
  };
  const saveEdit = async () => {
    if (!editFor) return;
    setEditSaving(true);
    try {
      await api.brokerEditPlaudNote({
        kind: editFor.store || (editFor.kind === 'call' ? 'call' : 'event'),
        id: editFor.id, project_id: editFor.project_id || '',
        summary: editSummary, title: editTitle,
      });
      toast.success('Plaud-Notiz aktualisiert');
      setEditFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setEditSaving(false); }
  };

  const load = () => {
    api.brokerEngagements().then(r => setData(r.data)).catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    const processing = (data?.engagements || []).some(e => e.plaud?.status === 'processing');
    if (!processing) { pollCount.current = 0; return; }
    if (pollCount.current >= 24) return;
    const t = setTimeout(() => { pollCount.current += 1; load(); }, 5000);
    return () => clearTimeout(t);
  }, [data]);

  const openPicker = (key) => {
    setPlaudPickerFor(k => (k === key ? null : key));
    if (recordings === null && !recsLoading) {
      setRecsLoading(true);
      api.getPlaudRecordings(14).then(r => setRecordings(r.data || []))
        .catch(err => {
          setRecordings(null);
          toast.error(err.response?.status === 409
            ? 'Plaud ist nicht verbunden — bitte auf der Team-Meeting-Seite verbinden'
            : 'Plaud-Aufnahmen konnten nicht geladen werden');
        })
        .finally(() => setRecsLoading(false));
    }
  };

  const attachPlaud = async (e, rec) => {
    setBusyRec(rec.id);
    try {
      await api.brokerPlaudSummarize({
        kind: e.store || (e.kind === 'call' ? 'call' : 'event'),
        id: e.id, project_id: e.project_id || '',
        recording_id: rec.id, recording_name: rec.name,
      });
      toast.success('Aufnahme angehängt — die KI erstellt jetzt die Zusammenfassung…');
      setPlaudPickerFor(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Anhängen fehlgeschlagen'); }
    finally { setBusyRec(''); }
  };

  const linkBody = (e, extra) => ({
    kind: e.store || (e.kind === 'call' ? 'call' : 'event'),
    id: e.id, project_id: e.project_id || '', ...extra,
  });

  const confirmLink = async (e, s) => {
    setLinkBusy(s.project_id);
    try {
      const r = await api.brokerConfirmDealLink(linkBody(e, { target_project_id: s.project_id }));
      toast.success(r.data.mandate_linked
        ? `Mit „${r.data.project_name}" verknüpft — Gespräch auf dem Deal & Mandat dem Broker zugeordnet`
        : `Mit „${r.data.project_name}" verknüpft — das Gespräch erscheint jetzt auf dem Deal`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Verknüpfen fehlgeschlagen'); }
    finally { setLinkBusy(''); }
  };

  const dismissSug = async (e, s) => {
    try {
      await api.brokerDismissDealLink(linkBody(e, {
        target_project_id: s.project_id || '', proposal_id: s.proposal_id || '' }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const createFromInbox = async (e, s) => {
    setLinkBusy(s.proposal_id);
    try {
      const r = await api.createDealFromProposal(s.proposal_id);
      await api.brokerConfirmDealLink(linkBody(e, {
        target_project_id: r.data.id, proposal_id: s.proposal_id }));
      toast.success(`Deal „${r.data.name}" angelegt & Gespräch verknüpft`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
    finally { setLinkBusy(''); }
  };

  const deleteMeeting = async (e) => {
    if (!window.confirm(`Meeting mit ${e.broker_name || 'Broker'} löschen?`)) return;
    setDeletingId(e.id);
    try {
      await api.deleteBrokerMeeting(e.id);
      toast.success('Meeting gelöscht');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen'); }
    finally { setDeletingId(''); }
  };

  const markAsBroker = async (c) => {
    setBusyId(c.contact_id);
    try {
      await api.updateContact(c.contact_id, { type: 'Broker' });
      toast.success(`${c.name} ist jetzt als Broker markiert — Gespräche erscheinen in der Liste`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setBusyId(''); }
  };

  const rows = useMemo(() => {
    const all = data?.engagements || [];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter(e =>
      (e.broker_name || '').toLowerCase().includes(term) ||
      (e.broker_company || '').toLowerCase().includes(term) ||
      (e.project_name || '').toLowerCase().includes(term) ||
      (e.title || '').toLowerCase().includes(term) ||
      (e.notes || '').toLowerCase().includes(term) ||
      (e.plaud?.summary || '').toLowerCase().includes(term));
  }, [data, q]);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-2 mb-1">
        <Handshake className="w-5 h-5 text-primary" />
        <h1 className="font-heading text-xl text-foreground">Broker-Gespräche</h1>
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        Alle Telefonate &amp; Meetings mit Broker-Kontakten — neueste zuerst, Notizen zum Aufklappen.
        Fehlt ein Call? In <Link to="/calls" className="text-sky-400 hover:underline">Call Analytics</Link> per „Kontakt zuweisen" dem Broker zuordnen.
      </p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Nach Broker, Firma, Deal oder Notiz suchen…"
            data-testid="broker-calls-search"
            className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs pl-8" />
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-broker-meeting-btn"
          className="h-8 text-xs rounded-sm bg-violet-600 hover:bg-violet-500 text-white">
          <Plus className="w-3.5 h-3.5 mr-1" /> Meeting hinzufügen
        </Button>
        <Button size="sm" variant="outline" onClick={syncCalendar} disabled={calSyncing}
          data-testid="sync-broker-calendar-btn" title="Outlook-Kalender jetzt nach Broker-Meetings scannen (läuft sonst automatisch alle 15 Min.)"
          className="h-8 text-xs rounded-sm border-sky-500/40 text-sky-300 hover:bg-sky-500/10">
          {calSyncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Kalender scannen
        </Button>
      </div>

      {(data?.non_broker_contacts || []).length > 0 && (
        <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm px-3 py-2.5 mb-4"
          data-testid="broker-calls-untyped-hint">
          <p className="text-[11px] text-amber-300 mb-1.5">
            Diese Kontakte haben zugeordnete Gespräche, sind aber nicht als „Broker" markiert — deshalb erscheinen sie hier nicht:
          </p>
          <div className="space-y-1">
            {data.non_broker_contacts.map(c => (
              <div key={c.contact_id} className="flex items-center gap-2 flex-wrap text-xs">
                <span className="text-foreground">{c.name}</span>
                {c.company && <span className="text-muted-foreground truncate">· {c.company}</span>}
                <span className="text-[10px] text-muted-foreground">
                  (Typ: {c.type || '—'} · {c.calls} Gespräch{c.calls !== 1 ? 'e' : ''})
                </span>
                <button onClick={() => markAsBroker(c)} disabled={busyId === c.contact_id}
                  data-testid={`mark-as-broker-${c.contact_id}`}
                  className="flex items-center gap-1 text-[10px] border border-amber-500/40 rounded-sm px-2 py-0.5 text-amber-300 hover:bg-amber-500/15 transition-colors">
                  {busyId === c.contact_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Handshake className="w-3 h-3" />}
                  Als Broker markieren
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : rows.length === 0 ? (
        <div className="border border-border rounded-sm bg-card px-4 py-10 text-center" data-testid="broker-calls-empty">
          <p className="text-sm text-muted-foreground">
            {q ? 'Keine Treffer für deine Suche.' : 'Noch keine Broker-Gespräche erfasst.'}
          </p>
          {!q && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Calls erscheinen hier automatisch, sobald sie einem Kontakt vom Typ „Broker" zugeordnet sind (per Telefonnummer oder manuell in Call Analytics).
            </p>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-sm bg-card divide-y divide-border" data-testid="broker-calls-list">
          {rows.map((e, i) => {
            const key = e.id || i;
            const plaudReady = !!e.plaud && (e.plaud.status || 'ready') === 'ready';
            const expandable = plaudReady || !!e.notes;
            const isOpen = expanded === key;
            return (
              <div key={key} className="px-4 py-3" data-testid={`broker-call-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="mt-0.5"><KindIcon e={e} /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-foreground font-medium" data-testid={`broker-call-name-${i}`}>
                        {e.broker_name || 'Unbekannt'}
                      </span>
                      {e.broker_company && <span className="text-xs text-muted-foreground truncate">· {e.broker_company}</span>}
                      <span className="text-[11px] font-mono text-muted-foreground">{fmtDate(e.at)}</span>
                      {e.kind === 'call' && (
                        <span className="text-[11px] text-muted-foreground">
                          {e.direction === 'in' ? (e.answered ? 'eingehend' : 'verpasst') : 'ausgehend'}
                          {e.duration_s > 0 && ` · ${fmtDur(e.duration_s)}`}
                        </span>
                      )}
                      {e.kind !== 'call' && (
                        <span className="text-[11px] text-muted-foreground">
                          {e.title || (e.kind === 'meeting' ? 'Meeting' : 'Notiz')}
                          {e.duration_s > 0 && ` · ${fmtDur(e.duration_s)}`}
                        </span>
                      )}
                      {e.source === 'calendar' && (
                        <span className="text-[9px] font-mono border border-sky-500/30 bg-sky-500/10 text-sky-300/80 rounded px-1 py-0.5"
                          title="Automatisch aus dem Outlook-Kalender übernommen">Outlook</span>
                      )}
                      <span className="ml-auto flex items-center gap-2 shrink-0">
                        {e.project_name && (
                          <Link to={`/projects/${e.project_id}`} data-testid={`broker-call-deal-${i}`}
                            className="text-[11px] text-sky-400 hover:underline truncate max-w-[180px]">{e.project_name}</Link>
                        )}
                        {e.created_by && <span className="text-[10px] text-muted-foreground/70">{e.created_by}</span>}
                        {(e.is_manual || e.source === 'calendar') && (
                          <button onClick={() => deleteMeeting(e)} disabled={deletingId === e.id}
                            data-testid={`broker-call-delete-${i}`}
                            title={e.is_manual ? 'Manuelles Meeting löschen' : 'Kalender-Meeting entfernen (wird nicht erneut importiert)'}
                            className="p-0.5 text-muted-foreground/60 hover:text-red-400 transition-colors">
                            {deletingId === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          </button>
                        )}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      {expandable && (
                        <button onClick={() => setExpanded(isOpen ? null : key)}
                          data-testid={`broker-call-toggle-${i}`}
                          className="flex items-center gap-1 mt-1 text-[11px] text-violet-300 hover:text-violet-200 transition-colors max-w-full">
                          {plaudReady ? <FileAudio className="w-3 h-3 shrink-0" /> : <StickyNote className="w-3 h-3 shrink-0" />}
                          <span className="truncate">{plaudReady ? e.plaud.title : 'Notizen'}</span>
                          <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                      {e.plaud?.status === 'processing' && (
                        <span className="flex items-center gap-1 mt-1 text-[11px] text-violet-300"
                          data-testid={`broker-call-plaud-processing-${i}`}>
                          <Loader2 className="w-3 h-3 animate-spin" /> KI fasst zusammen…
                        </span>
                      )}
                      {e.plaud?.status === 'failed' && (
                        <span className="mt-1 text-[11px] text-red-400" data-testid={`broker-call-plaud-failed-${i}`}>
                          KI-Zusammenfassung fehlgeschlagen
                        </span>
                      )}
                      {(e.kind === 'call' || e.kind === 'meeting') && e.plaud?.status !== 'processing' && (
                        <button onClick={() => openPicker(key)} data-testid={`broker-call-plaud-add-${i}`}
                          className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground hover:text-violet-300 transition-colors">
                          <FileAudio className="w-3 h-3" />
                          {e.plaud ? (e.plaud.status === 'failed' ? 'erneut versuchen' : 'Plaud-Notiz ersetzen') : 'Plaud-Notiz hinzufügen'}
                        </button>
                      )}
                      {plaudReady && (
                        <button onClick={() => openEdit(e)} data-testid={`broker-call-plaud-edit-${i}`}
                          className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground hover:text-violet-300 transition-colors">
                          <Pencil className="w-3 h-3" /> Notiz bearbeiten
                        </button>
                      )}
                      <button onClick={() => openManualLink(e)} data-testid={`broker-call-manual-link-${i}`}
                        className="flex items-center gap-1 mt-1 text-[11px] text-muted-foreground hover:text-sky-300 transition-colors">
                        <Link2 className="w-3 h-3" /> Deal manuell verknüpfen
                      </button>
                    </div>
                    {plaudPickerFor === key && (
                      <PlaudPicker recordings={recordings} loading={recsLoading} busyRec={busyRec}
                        onPick={(r) => attachPlaud(e, r)} onClose={() => setPlaudPickerFor(null)} />
                    )}
                    {plaudReady && (
                      <DealSuggestions e={e} idx={i} linkBusy={linkBusy}
                        onConfirm={confirmLink} onDismiss={dismissSug} onCreateFromInbox={createFromInbox} />
                    )}
                    {(e.plaud?.linked_deals || []).length > 0 && (
                      <div className="flex items-center gap-2 flex-wrap mt-1.5" data-testid={`broker-call-linked-deals-${i}`}>
                        {e.plaud.linked_deals.map(l => (
                          <Link key={l.project_id} to={`/projects/${l.project_id}`}
                            className="flex items-center gap-1 text-[10px] border border-emerald-500/30 bg-emerald-500/5 rounded-sm px-2 py-0.5 text-emerald-300 hover:bg-emerald-500/15 transition-colors">
                            <Link2 className="w-3 h-3" /> {l.name}
                          </Link>
                        ))}
                      </div>
                    )}
                    {isOpen && (
                      <div className="mt-1.5 border border-violet-500/25 bg-violet-500/5 rounded-sm p-2.5"
                        data-testid={`broker-call-notes-${i}`}>
                        <p className="text-xs text-foreground/90 whitespace-pre-wrap max-h-56 overflow-y-auto">
                          {plaudReady ? (e.plaud.summary || '—') : (e.notes || '—')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Manuelles Deal-Linking */}
      <Dialog open={!!manualLinkFor} onOpenChange={(o) => !o && setManualLinkFor(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="broker-manual-link-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
              <Link2 className="w-4 h-4 text-sky-400" /> Gespräch manuell mit Deal verknüpfen
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Das Gespräch wird als Event am ausgewählten Deal in „Meetings & Calls" ergänzt.
          </p>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={manualSearch} onChange={e => setManualSearch(e.target.value)}
              placeholder="Nach Deal-Name suchen…"
              data-testid="broker-manual-link-search"
              className="bg-secondary border-border text-foreground rounded-sm h-8 text-xs pl-8" />
          </div>
          <div className="max-h-72 overflow-y-auto border border-border rounded-sm divide-y divide-border">
            {manualLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            ) : manualHits.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6">
                {manualSearch ? 'Keine Treffer.' : 'Tippe einen Deal-Namen ein — oder lade die Vorschau …'}
              </p>
            ) : manualHits.map(p => (
              <button key={p.id} onClick={() => doManualLink(p)} disabled={linkBusy === p.id}
                data-testid={`broker-manual-link-result-${p.id}`}
                className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-secondary/40 transition-colors disabled:opacity-60">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground font-medium truncate">{p.name}</p>
                  {p.random_project_name && p.random_project_name !== p.name && (
                    <p className="text-[10px] text-muted-foreground truncate">{p.random_project_name}</p>
                  )}
                </div>
                {linkBusy === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-400" />
                  : <Check className="w-3.5 h-3.5 text-sky-400 opacity-0 group-hover:opacity-100" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Plaud-Notiz bearbeiten */}
      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl" data-testid="broker-plaud-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base text-foreground flex items-center gap-2">
              <Pencil className="w-4 h-4 text-violet-400" /> Plaud-Notiz bearbeiten
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground -mt-1">
            Korrigiere Transkriptionsfehler (z. B. „OLTER" → „OLTA"). Die Änderung überschreibt die Zusammenfassung überall wo sie erscheint (Broker-Gespräche und verlinkte Deals).
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Titel</label>
              <Input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                data-testid="plaud-edit-title"
                className="bg-secondary border-border text-foreground rounded-sm text-xs h-8" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Zusammenfassung</label>
              <Textarea value={editSummary} onChange={e => setEditSummary(e.target.value)} rows={12}
                data-testid="plaud-edit-summary"
                className="bg-secondary border-border text-foreground rounded-sm text-xs font-mono" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditFor(null)} data-testid="plaud-edit-cancel"
                className="text-xs h-8">Abbrechen</Button>
              <Button onClick={saveEdit} disabled={editSaving} data-testid="plaud-edit-save"
                className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-8">
                {editSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddMeetingDialog open={addOpen} onOpenChange={setAddOpen} onDone={load} />
    </div>
  );
}

